import React, { useState } from "react";

type Item = { id: number; name: string; amount: number };

interface SelectedItem {
  id: number;
  name: string;
  price: number;
  qty: number;
  total: number;
}

interface Props {
  items: Item[];
  defaultValue?: SelectedItem[];
  onChange: (items: SelectedItem[], total: number) => void;
}

const ProfitMultiPresetPicker = ({ items, defaultValue = [], onChange }: Props) => {
  const [selected, setSelected] = useState<SelectedItem[]>(defaultValue);

  function handleCheck(item: Item, checked: boolean) {
    if (checked) {
      setSelected(sels => [...sels, { id: item.id, name: item.name, price: item.amount, qty: 1, total: item.amount }]);
    } else {
      setSelected(sels => sels.filter(s => s.id !== item.id));
    }
  }

  function handleQtyChange(itemId: number, qty: number) {
    setSelected(sels => sels.map(s =>
      s.id !== itemId ? s : { ...s, qty, total: qty * s.price }
    ));
  }

  React.useEffect(() => {
    const total = selected.reduce((sum, s) => sum + s.total, 0);
    onChange(selected, total);
  }, [selected, onChange]);

  return (
    <div className="flex flex-col gap-3 mt-1">
      {items.map(item => {
        const isChecked = selected.some(s => s.id === item.id);
        const sel = selected.find(s => s.id === item.id);
        return (
          <label
            key={item.id}
            className="flex items-center gap-4 border rounded-lg px-4 py-3 bg-muted hover:bg-background cursor-pointer"
          >
            <input
              type="checkbox"
              checked={isChecked}
              onChange={e => handleCheck(item, e.target.checked)}
              className="form-checkbox h-5 w-5 accent-primary"
            />
            <span className="flex-1 min-w-0 truncate font-medium">{item.name}</span>
            <span className="font-mono font-bold text-primary">${item.amount}</span>
            {isChecked && (
              <span className="flex items-center gap-1 ml-3">
                <input
                  type="number"
                  min={1}
                  value={sel?.qty || 1}
                  onChange={e => handleQtyChange(item.id, Math.max(1, parseInt(e.target.value) || 1))}
                  className="w-16 px-2 border rounded bg-white py-1 font-mono text-base text-primary"
                  style={{width:50}}
                />
                <span className="text-xs text-muted-foreground">qty</span>
              </span>
            )}
            {isChecked && (
              <span className="ml-3 text-base font-bold text-green-700">${sel?.total || item.amount}</span>
            )}
          </label>
        );
      })}
      <div className="text-lg font-semibold text-right px-2 pt-3">
        Total: <span className="font-mono text-primary">${selected.reduce((sum,s)=>sum+s.total,0)}</span>
      </div>
    </div>
  );
};

export default ProfitMultiPresetPicker;
